/**
 * Nivel de experiencia de la superficie pública: `full` o `reduced`.
 *
 * ── Qué problema resuelve ────────────────────────────────────────────────────────────
 * La home tiene ~10 animaciones infinitas, dos videos rotando y dos loops de puntero.
 * En una máquina que da el ancho eso ES la identidad de la marca y se queda como está.
 * En una que no, hay que bajar el costo visual SIN sacar contenido, CTAs, navegación ni
 * accesibilidad. `reduced` no es otra home: es la misma con menos movimiento.
 *
 * ── Por qué no `mobile == gama baja` ─────────────────────────────────────────────────
 * Un celular puede tener 8 núcleos y 8 GB, y una notebook de 2013 dos núcleos y 4 GB.
 * El tamaño de pantalla no dice nada del equipo. Acá se decide con señales del equipo y
 * de la red, y el viewport pesa 1 punto sobre 3: acompaña, nunca decide solo.
 *
 * ── Las tres capas, en orden de precedencia ──────────────────────────────────────────
 *
 * CAPA 1 — overrides duros. No entran al puntaje: cortocircuitan.
 *   · `prefers-reduced-motion: reduce`  → el usuario pidió menos movimiento en su SO.
 *     Gana sobre todo, incluso en una máquina potente. Es una preferencia, no una
 *     medición, y no se discute.
 *   · `Save-Data`                       → el usuario pidió ahorrar datos.
 *   · localStorage `academia-experience`→ decisión explícita (y la puerta para QA).
 *   Estas tres las evalúa el script inline de `root.tsx` ANTES del primer pintado, así
 *   que quien pidió no ver movimiento no ve ni un frame de movimiento. Ver `INLINE_SNIPPET`.
 *
 * CAPA 2 — puntaje de capacidad, sólo si no hubo override. Ver `capabilityScore()`.
 *
 * CAPA 3 — verificación en runtime. Sólo DEGRADA, nunca promueve: promover a mitad de
 *   scroll reiniciaría animaciones y se leería como un bug. Ver `startRuntimeProbe()`.
 *
 * ── Fallback cuando una API no existe ────────────────────────────────────────────────
 * Regla única: **una señal ausente suma 0**. Nunca empuja hacia `reduced` por no existir.
 * El default sin información es `full` — la línea base es la experiencia diseñada y sólo
 * se baja con evidencia. Safari y Firefox no exponen `deviceMemory` ni Network
 * Information: ahí mandan `prefers-reduced-motion`, `hardwareConcurrency` (que Safari sí
 * tiene) y sobre todo la capa 3, que es la única universal y la que agarra los casos que
 * ninguna señal estática reporta (un iPhone viejo, una pestaña compitiendo con una
 * videollamada, el modo de bajo consumo).
 */

export type ExperienceLevel = "full" | "reduced";

/** Atributo en `<html>`. Es el contrato con el CSS: `html[data-experience="reduced"]`. */
export const EXPERIENCE_ATTR = "data-experience";
export const EXPERIENCE_STORAGE_KEY = "academia-experience";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Las tres condiciones de la CAPA 1, como texto, para que `root.tsx` las corra inline
 * antes del primer pintado. Vive acá y no allá para que el criterio tenga UN solo lugar
 * donde leerse. Es deliberadamente corto: todo lo que no sea un override duro se decide
 * después de hidratar, donde se puede escribir en TypeScript y testear.
 */
export const EXPERIENCE_INLINE_SNIPPET = `
(function () {
  try {
    var stored = localStorage.getItem('${EXPERIENCE_STORAGE_KEY}');
    var reduced =
      stored === 'reduced' ||
      (stored !== 'full' && (
        matchMedia('${REDUCED_MOTION_QUERY}').matches ||
        (navigator.connection && navigator.connection.saveData === true)
      ));
    document.documentElement.setAttribute('${EXPERIENCE_ATTR}', reduced ? 'reduced' : 'full');
  } catch (_) {
    document.documentElement.setAttribute('${EXPERIENCE_ATTR}', 'full');
  }
})();
`;

const listeners = new Set<(level: ExperienceLevel) => void>();

function read(): ExperienceLevel {
  if (typeof document === "undefined") return "full";
  return document.documentElement.getAttribute(EXPERIENCE_ATTR) === "reduced"
    ? "reduced"
    : "full";
}

function write(level: ExperienceLevel) {
  if (typeof document === "undefined" || read() === level) return;
  document.documentElement.setAttribute(EXPERIENCE_ATTR, level);
  for (const listener of listeners) listener(level);
}

/** Sólo degrada. La única promoción posible es apagar `prefers-reduced-motion`. */
function degrade() {
  write("reduced");
}

export function getExperienceLevel(): ExperienceLevel {
  return read();
}

export function subscribeExperience(listener: (level: ExperienceLevel) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Preferencia explícita del usuario. `null` = que decida la detección. */
export function getExperienceOverride(): ExperienceLevel | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(EXPERIENCE_STORAGE_KEY);
    return stored === "full" || stored === "reduced" ? stored : null;
  } catch {
    return null;
  }
}

export function setExperienceOverride(level: ExperienceLevel | null) {
  if (typeof window === "undefined") return;
  try {
    if (level) window.localStorage.setItem(EXPERIENCE_STORAGE_KEY, level);
    else window.localStorage.removeItem(EXPERIENCE_STORAGE_KEY);
  } catch {
    // Modo privado de Safari, cuota llena: la preferencia no persiste y ya está.
  }
  write(level ?? (capabilityScore() >= REDUCED_THRESHOLD ? "reduced" : "full"));
}

type NetworkInformationLike = {
  saveData?: boolean;
  effectiveType?: string;
  downlink?: number;
};

/** Network Information API: sólo Chromium. En el resto devuelve `null` y no suma nada. */
function connection(): NetworkInformationLike | null {
  if (typeof navigator === "undefined") return null;
  try {
    return (navigator as Navigator & { connection?: NetworkInformationLike }).connection ?? null;
  } catch {
    return null;
  }
}

function matches(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

export function prefersReducedMotion(): boolean {
  return matches(REDUCED_MOTION_QUERY);
}

export function prefersSaveData(): boolean {
  return connection()?.saveData === true;
}

/** A partir de acá el equipo se considera limitado. */
export const REDUCED_THRESHOLD = 3;

/**
 * Interruptor de la CAPA 3 (la sonda de runtime). APAGADA por defecto (2026-08-18).
 *
 * Está apagada porque es la única capa que puede degradar por algo que nadie declara y
 * que no se puede reproducir desde otra máquina — y ya dejó la home estática una vez en
 * uso real. La sonda quedó recalibrada y sus dos falsos positivos verificados y cerrados
 * (ver `startRuntimeProbe`), pero el reparto de costos no es simétrico:
 *
 *  · Un falso positivo apaga la home de la marca para visitantes reales, en silencio y
 *    sin que nadie se entere hasta que alguien la mira.
 *  · Un falso negativo le da la experiencia completa a un equipo flojo, que la ve con
 *    menos fluidez. Y el caso grave igual lo agarra la capa 2: dos núcleos, 2 GB o una
 *    red 2G/3G degradan sin necesidad de medir nada.
 *
 * Con esa asimetría, el default seguro es no medir. Para volver a prenderla alcanza con
 * poner esto en `true`; la sonda está entera y tipada, no borrada.
 */
const RUNTIME_PROBE_ENABLED: boolean = false;

/**
 * CAPA 2. Cada señal suma sólo si el navegador la expone; si falta, suma 0.
 *
 * Los pesos están elegidos para que UNA señal fuerte alcance (red 2G, 2 GB de RAM, 2
 * núcleos: 3 puntos) y las señales débiles necesiten acompañarse. El viewport vale 1:
 * acompaña, nunca decide.
 *
 * ── Recalibrado 2026-08-18 ───────────────────────────────────────────────────────────
 * `deviceMemory <= 4` y `hardwareConcurrency <= 4` valían 2 puntos cada uno, así que una
 * notebook de 4 núcleos con 4 GB —o sea una máquina de oficina cualquiera, y también un
 * Android de gama media -sumaba 4 y caía en `reduced` sin haber renderizado un frame.
 * Ninguna de las dos es una señal fuerte: `deviceMemory` viene cuantizado y topeado en 8
 * (un equipo con 6 GB reporta 4), y 4 hilos es lo normal en cualquier portátil moderno.
 * Ahora valen 1 —acompañan— y hacen falta DOS señales débiles más para degradar.
 */
export function capabilityScore(): number {
  if (typeof navigator === "undefined") return 0;
  let score = 0;

  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (typeof memory === "number") {
    if (memory <= 1) score += 3;
    else if (memory <= 2) score += 2;
    else if (memory <= 4) score += 1;
  }

  const cores = navigator.hardwareConcurrency;
  if (typeof cores === "number" && cores > 0) {
    if (cores <= 2) score += 3;
    else if (cores <= 4) score += 1;
  }

  const net = connection();
  const effectiveType = net?.effectiveType;
  if (effectiveType === "slow-2g" || effectiveType === "2g") score += 3;
  else if (effectiveType === "3g") score += 2;

  if (typeof net?.downlink === "number" && net.downlink > 0 && net.downlink < 1.5) score += 2;

  // Empujón débil: pantalla chica CON puntero grueso. Un desktop angosto no cuenta.
  if (matches("(pointer: coarse)") && window.innerWidth < 420) score += 1;

  return score;
}

/**
 * CAPA 3 — lo que el equipo hace de verdad, no lo que dice ser.
 *
 * Degrada sólo si el equipo NO PUEDE sostener la página: hacen falta TRES ventanas
 * seguidas de 1 s por debajo de 20 fps, o una ventana con 5 tareas largas de más de
 * 150 ms. Con menos que eso no se toca nada.
 *
 * ═══ Por qué es tan conservadora: dos falsos positivos reproducidos (2026-08-18) ═══
 *
 * La primera versión tomaba UNA muestra de 1,5 s apenas terminaba de cargar y degradaba
 * si el frame promedio pasaba de 24 ms (42 fps). Eso dejó la home estática de verdad, en
 * uso real, y por dos motivos distintos:
 *
 *  1. MEDÍA EN EL PEOR MOMENTO. La ventana caía justo cuando el hero está decodificando
 *     su primer clip y entran las imágenes diferidas. Un bajón de un segundo ahí decidía
 *     la sesión entera. Medido contra el sitio: el atributo pasaba a `reduced` a los
 *     3,8 s y el video del hero quedaba en pausa con `readyState 4` — cargado y quieto,
 *     que es exactamente el síntoma que se reportó.
 *
 *  2. rAF NO MIDE LA MÁQUINA, MIDE EL VSYNC Y EL THROTTLING. Con la pestaña en segundo
 *     plano el navegador baja `requestAnimationFrame` a una fracción, así que abrir la
 *     home con un cmd-click, o restaurar una sesión con varias pestañas, la degradaba
 *     siempre. Reproducido: abierta en background daba `reduced`, y al traerla al frente
 *     NO se recuperaba porque la decisión es de una sola vía. Lo mismo pasa con una
 *     pantalla a 30 Hz o con el ahorro de energía del sistema, donde 33 ms por frame es
 *     lo normal y no significa nada malo.
 *
 * De ahí las cuatro reglas de abajo. Si alguna vez hay que hacerla más sensible, hay que
 * volver a pasar por estos dos casos: son los que la rompieron.
 */
function startRuntimeProbe(): () => void {
  /** Post-arranque: el pico de carga y la decodificación del primer clip ya pasaron. */
  const START_DELAY_MS = 5000;
  const WINDOW_MS = 1000;
  /** 20 fps. No es "no llega a 60": es jank que se ve. */
  const SLOW_FRAME_MS = 50;
  /**
   * Piso de frames para que una ventana cuente como evidencia.
   *
   * `document.visibilityState` no alcanza para detectar el throttling: una ventana tapada
   * por otra, minimizada o en otro escritorio sigue reportándose "visible" y aun así el
   * navegador le baja `requestAnimationFrame` a ~1 Hz. La diferencia se ve en el CONTEO:
   * un equipo lento de verdad igual entrega ~10 frames en un segundo, uno throttleado
   * entrega uno o dos. Con menos de 5, la ventana no dice nada del equipo y se descarta.
   */
  const MIN_FRAMES = 5;
  /** Ventanas malas seguidas para decidir. Una sola es ruido. */
  const BAD_WINDOWS_NEEDED = 3;
  const LONG_TASK_MS = 150;
  const LONG_TASK_LIMIT = 5;
  /**
   * Techo de ventanas. Dos motivos: con esta evidencia ya alcanza para decidir, y sobre
   * todo evita dos cosas feas — un loop de `requestAnimationFrame` corriendo para siempre
   * (justo el tipo de trabajo de fondo que esta pantalla vino a sacar) y una degradación
   * que aparezca de la nada a los diez minutos porque el usuario abrió otra aplicación.
   */
  const MAX_WINDOWS = 12;

  let stopped = false;
  let observer: PerformanceObserver | null = null;
  let raf = 0;
  let timer = 0;
  let badWindows = 0;
  let windowsRun = 0;

  const stop = () => {
    stopped = true;
    observer?.disconnect();
    observer = null;
    if (raf) cancelAnimationFrame(raf);
    if (timer) window.clearTimeout(timer);
    raf = 0;
    timer = 0;
  };

  const visible = () => document.visibilityState === "visible";

  const sampleWindow = () => {
    if (stopped) return;
    if (windowsRun >= MAX_WINDOWS) {
      stop();
      return;
    }
    // Regla 2: sin pestaña visible no se mide. Lo que se leería es el throttling del
    // navegador, no el equipo. Se reintenta más tarde en vez de abandonar: la pestaña
    // puede pasar al frente en cualquier momento.
    if (!visible()) {
      badWindows = 0;
      timer = window.setTimeout(sampleWindow, WINDOW_MS);
      return;
    }
    windowsRun += 1;

    let longTasks = 0;
    try {
      observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration >= LONG_TASK_MS) longTasks += 1;
        }
        if (longTasks >= LONG_TASK_LIMIT) {
          degrade();
          stop();
        }
      });
      // Sin `buffered`: sólo cuenta lo que pase DENTRO de la ventana.
      observer.observe({ type: "longtask" });
    } catch {
      observer = null;
    }

    let frames = 0;
    let accumulated = 0;
    let last = performance.now();
    const started = last;

    const tick = () => {
      if (stopped) return;
      // Si la pestaña se fue a segundo plano a mitad de ventana, la muestra se tira: el
      // salto de tiempo que deja el throttling la haría "mala" siempre.
      if (!visible()) {
        badWindows = 0;
        observer?.disconnect();
        observer = null;
        timer = window.setTimeout(sampleWindow, WINDOW_MS);
        return;
      }
      const now = performance.now();
      accumulated += now - last;
      last = now;
      frames += 1;

      if (now - started >= WINDOW_MS) {
        observer?.disconnect();
        observer = null;
        // Pocos frames = pestaña throttleada, no equipo lento: la ventana se descarta y
        // la racha se reinicia. Ver `MIN_FRAMES`.
        const inconclusive = frames < MIN_FRAMES;
        const slow = !inconclusive && accumulated / frames > SLOW_FRAME_MS;
        badWindows = slow ? badWindows + 1 : 0;
        if (badWindows >= BAD_WINDOWS_NEEDED) {
          degrade();
          stop();
          return;
        }
        // Sigue midiendo: una racha buena reinicia la cuenta y esto se apaga solo cuando
        // el usuario se va de la página.
        timer = window.setTimeout(sampleWindow, WINDOW_MS);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  };

  const arm = () => {
    if (stopped) return;
    timer = window.setTimeout(sampleWindow, START_DELAY_MS);
    // Cierre por reloj además del techo de ventanas: con la pestaña en segundo plano las
    // ventanas no se consumen (no se mide), y sin esto quedaría un temporizador
    // reintentando para siempre en una pestaña que quizás nunca vuelve al frente.
    window.setTimeout(stop, START_DELAY_MS + MAX_WINDOWS * WINDOW_MS * 3);
  };

  if (document.readyState === "complete") arm();
  else window.addEventListener("load", arm, { once: true });

  return stop;
}

/**
 * Arranca la detección. Se llama UNA vez, en el cliente, después de hidratar.
 *
 * El script inline ya resolvió los overrides duros antes del primer pintado, así que acá
 * sólo queda el puntaje y las sondas — y ninguno de los dos puede promover: si el inline
 * dijo `reduced` fue por una preferencia del usuario, y esa no se pisa.
 */
export function initExperienceLevel(): () => void {
  if (typeof window === "undefined") return () => {};

  const override = getExperienceOverride();

  const apply = () => {
    if (override) {
      write(override);
      return;
    }
    if (prefersReducedMotion() || prefersSaveData()) {
      write("reduced");
      return;
    }
    write(capabilityScore() >= REDUCED_THRESHOLD ? "reduced" : "full");
  };

  apply();

  // Si el usuario cambia la preferencia en el SO, la página tiene que seguirlo sin
  // recargar. Acá SÍ puede promover: apagar "menos movimiento" es una acción explícita y
  // que la página vuelva a moverse es exactamente lo que se pidió.
  let unlistenMedia = () => {};
  try {
    const media = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => apply();
    media.addEventListener("change", onChange);
    unlistenMedia = () => media.removeEventListener("change", onChange);
  } catch {
    // matchMedia sin addEventListener (Safari viejo): se queda con el valor de arranque.
  }

  const stopProbe =
    !RUNTIME_PROBE_ENABLED || override || read() === "reduced" ? () => {} : startRuntimeProbe();

  return () => {
    unlistenMedia();
    stopProbe();
  };
}
